import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import axios from "../../axiosConfig";
import { useToast } from "../commons/toast/ToastContext";
import "../../styles/InviteDogOwnerModal.css";

const InviteDogOwnerModal = ({ isOpen, onClose, dogId, onInviteSent }) => {
    const { t } = useTranslation(['dogs', 'common']);
    const [email, setEmail] = useState("");
    const [error, setError] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const toast = useToast();

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!email.trim()) {
            setError(t('dogs:owners.invite.emailRequired'));
            return;
        }

        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email.trim())) {
            setError(t('dogs:owners.invite.emailInvalid'));
            return;
        }

        try {
            setSubmitting(true);
            setError("");
            const response = await axios.post("/api/v1/dog/trainer/invite-owner", {
                dogId,
                email: email.trim(),
            });

            if (response.data.status === "LINKED") {
                toast.success(response.data.message || t('dogs:ownerless.ownerLinked'));
            } else if (response.data.status === "SCHOOL_INVITATION_SENT") {
                toast.success(t('dogs:owners.invite.schoolInviteSent'));
            } else {
                toast.success(response.data.message || t('dogs:ownerless.inviteSent'));
            }

            setEmail("");
            onClose();
            if (onInviteSent) {
                onInviteSent(response.data);
            }
        } catch (err) {
            console.error("Error inviting owner:", err);
            if (err.response && err.response.data) {
                if (err.response.data.message) {
                    setError(err.response.data.message);
                } else if (typeof err.response.data === "string") {
                    setError(err.response.data);
                } else {
                    setError(t('dogs:ownerless.inviteError'));
                }
            } else {
                setError(t('dogs:ownerless.inviteError'));
            }
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        setEmail("");
        setError("");
        onClose();
    };

    if (!isOpen) return null;

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            handleCancel();
        }
    };

    return (
        <div className="inviteDogOwnerModal__overlay" onClick={handleOverlayClick}>
            <div className="inviteDogOwnerModal__content">
                <h2 className="inviteDogOwnerModal__title">{t('dogs:ownerless.inviteOwner')}</h2>
                <p className="inviteDogOwnerModal__description">
                    {t('dogs:ownerless.inviteDescription')}
                </p>

                {error && <div className="inviteDogOwnerModal__error">{error}</div>}

                <form className="inviteDogOwnerModal__form" onSubmit={handleSubmit}>
                    <div className="inviteDogOwnerModal__formGroup">
                        <label htmlFor="invite-dog-owner-email" className="inviteDogOwnerModal__label">
                            {t('dogs:owners.invite.emailLabel')}
                        </label>
                        <input
                            id="invite-dog-owner-email"
                            type="email"
                            className="inviteDogOwnerModal__input"
                            placeholder={t('dogs:ownerless.emailPlaceholder')}
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                        />
                    </div>

                    <div className="inviteDogOwnerModal__actions">
                        <button
                            type="submit"
                            className="inviteDogOwnerModal__submitButton"
                            disabled={submitting}
                        >
                            {submitting ? t('dogs:owners.invite.sending') : t('dogs:ownerless.sendInvite')}
                        </button>
                        <button
                            type="button"
                            className="inviteDogOwnerModal__cancelButton"
                            onClick={handleCancel}
                        >
                            {t('common:cancel')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default InviteDogOwnerModal;
